/**
 * connect/models.ts — types for Module #6, the Roamer Connect™.
 *
 * A platform MODULE, not a core engine. Connectivity and arrival services:
 * connectivity profile, SIM/eSIM plans, internet intelligence, arrival
 * transport, essential utilities, community insights, smart recommendations,
 * quick actions, and privacy controls. Honesty rules as everywhere: unknown
 * ⇒ null, intelligence derives only from recorded data, and providers are
 * registry interfaces prepared for future integrations — no implementations.
 */

// ─── Connectivity profile ──────────────────────────────────────────────────────

export type RoamingStatus = 'roaming' | 'local' | 'unknown';
export type BackupPreference = 'secondSim' | 'esim' | 'pocketWifi' | 'tethering' | 'none' | null;
export type WifiPreference = 'coworking' | 'cafe' | 'homeFiber' | 'mobileOnly' | null;

export interface ConnectivityProfile {
  currentCarrier: string | null;
  preferredEsimProvider: string | null;
  preferredSimProvider: string | null;
  preferredDataPlan: string | null;
  /** Minimum Mbps the user needs for remote work; null if unstated. */
  minInternetMbps: number | null;
  backupPreference: BackupPreference;
  wifiPreference: WifiPreference;
  roamingStatus: RoamingStatus;
}

export const DEFAULT_CONNECTIVITY_PROFILE: ConnectivityProfile = {
  currentCarrier: null,
  preferredEsimProvider: null,
  preferredSimProvider: null,
  preferredDataPlan: null,
  minInternetMbps: null,
  backupPreference: null,
  wifiPreference: null,
  roamingStatus: 'unknown',
};

// ─── SIM / eSIM plans (user-recorded; providers are future integrations) ─────

export const DATA_PLAN_TYPES = ['esim', 'localSim', 'touristSim', 'unlimited'] as const;
export type DataPlanType = (typeof DATA_PLAN_TYPES)[number];

export interface DataPlan {
  id: string;
  provider: string;
  name: string;
  type: DataPlanType;
  country: string;
  /** All commercial facts nullable — recorded, never invented. */
  priceMonthly: number | null;
  currency: string;
  dataGb: number | null; // null = unlimited or unknown; see type
  speedMbps: number | null;
  expiresAt: number | null;
  active: boolean;
  notes: string;
  addedAt: number;
}

export interface ActivationGuide {
  id: string;
  provider: string;
  country: string;
  title: string;
  steps: string[];
  /** Where the guide came from — user notes or a named community source. */
  source: string;
  addedAt: number;
}

export interface PlanComparison {
  planIds: string[];
  providers: string[];
  rows: { label: string; values: (string | number | boolean | null)[] }[];
  /** Cheapest plan name when all prices are recorded in one currency; null otherwise. */
  cheapest: string | null;
}

// ─── Internet intelligence (derived from recorded reports only) ───────────────

export interface SpeedReport {
  id: string;
  city: string;
  downloadMbps: number;
  /** Community-verified vs the user's own unverified measurement. */
  verified: boolean;
  venue: string | null; // e.g. a coworking space or cafe
  reportedAt: number;
}

/** City-level facts the user or a future provider has recorded; all nullable. */
export interface CityConnectivityFacts {
  city: string;
  mobileCoverageNote: string | null;
  fiberAvailable: boolean | null;
  powerReliabilityNote: string | null;
  publicWifiNote: string | null;
  updatedAt: number;
}

export interface InternetIntelligence {
  city: string;
  reportCount: number;
  averageDownloadMbps: number | null;
  verifiedAverageMbps: number | null;
  verifiedShare: number | null; // 0..1
  /** 'stable' | 'variable' from recorded spread; null under 3 reports. */
  stability: 'stable' | 'variable' | null;
  mobileCoverageNote: string | null;
  fiberAvailable: boolean | null;
  powerReliabilityNote: string | null;
  publicWifiNote: string | null;
}

// ─── Arrival services & essential utilities (recorded places) ─────────────────

export const TRANSPORT_KINDS = [
  'airportTransport',
  'publicTransit',
  'taxi',
  'rideSharing',
  'scooterRental',
  'bikeRental',
  'walkingMap',
] as const;
export type TransportKind = (typeof TRANSPORT_KINDS)[number];

export const UTILITY_KINDS = [
  'atm',
  'bank',
  'currencyExchange',
  'pharmacy',
  'hospital',
  'policeStation',
  'embassy',
  'groceryStore',
  'laundry',
  'gym',
  'coworking',
] as const;
export type UtilityKind = (typeof UTILITY_KINDS)[number];

export type PlaceKind = TransportKind | UtilityKind;

export interface ConnectPlace {
  id: string;
  kind: PlaceKind;
  name: string;
  city: string;
  address: string | null;
  url: string | null;
  notes: string;
  saved: boolean; // "Save Utility" quick action
  addedAt: number;
}

// ─── Community insights (Roamer Reports™ — recorded & sourced) ────────────────

export interface RoamerReport {
  id: string;
  city: string;
  topic: string; // e.g. "Best mobile carrier in Bangkok"
  summary: string;
  /** Named origin — a community post, a person, the user. Never fabricated. */
  source: string;
  verified: boolean;
  reportedAt: number;
}

// ─── Smart recommendations ─────────────────────────────────────────────────────

export interface ConnectRecommendation {
  /** Deterministic — stable while the condition holds. */
  id: string;
  message: string;
  /** The recorded data it derives from. Never empty. */
  basis: string[];
  severity: 'info' | 'nudge' | 'important';
}

// ─── Quick actions (available only when backing data exists) ──────────────────

export const QUICK_ACTION_KINDS = [
  'compareEsims',
  'findAtm',
  'navigateToEmbassy',
  'openTransitMap',
  'bookAirportRide',
  'locatePharmacy',
  'findCoworking',
  'saveUtility',
] as const;
export type QuickActionKind = (typeof QUICK_ACTION_KINDS)[number];

export interface QuickAction {
  kind: QuickActionKind;
  label: string;
  /** true only when recorded data can back the action right now. */
  available: boolean;
  /** Why it is (un)available — e.g. "2 eSIM plans recorded" / "no ATMs recorded in Bangkok". */
  reason: string;
}

// ─── Providers (future integrations — interfaces only) ────────────────────────

export const CONNECT_PROVIDER_KINDS = [
  'esim',
  'localSim',
  'touristSim',
  'dataPricing',
  'coverageMap',
  'transit',
  'places',
] as const;
export type ConnectProviderKind = (typeof CONNECT_PROVIDER_KINDS)[number];

export interface ConnectProvider {
  id: string;
  name: string;
  kind: ConnectProviderKind;
  fetchData(city: string): Promise<unknown[]>;
}

// ─── Privacy ───────────────────────────────────────────────────────────────────

/** Private by default — everything shared must be opted into. */
export interface ConnectPrivacySettings {
  /** Off ⇒ arrival briefings never publish the city on the Core bus. */
  locationSharingEnabled: boolean;
  /** Off ⇒ connectivity profile is excluded from exports/shares. */
  shareConnectivityPreferences: boolean;
  /** Off ⇒ no travel (arrival) history is stored at all. */
  trackTravelHistory: boolean;
}

export const DEFAULT_CONNECT_PRIVACY: ConnectPrivacySettings = {
  locationSharingEnabled: false,
  shareConnectivityPreferences: false,
  trackTravelHistory: false,
};

export interface ArrivalRecord {
  city: string;
  country: string | null;
  arrivedAt: number;
}

// ─── Export ────────────────────────────────────────────────────────────────────

export interface ConnectExport {
  exportedAt: number;
  /** null when shareConnectivityPreferences is off. */
  profile: ConnectivityProfile | null;
  plans: DataPlan[];
  guides: ActivationGuide[];
  speedReports: SpeedReport[];
  cityFacts: CityConnectivityFacts[];
  places: ConnectPlace[];
  reports: RoamerReport[];
  /** empty when trackTravelHistory is off. */
  travelHistory: ArrivalRecord[];
}
